import { useTranslation } from 'react-i18next'
import { Pillow } from '@/components/Pillow/Pillow'
import { ProductDetails } from '@/components/ProductCard/components/ProductDetails'
import { ProductDocuments } from '@/components/ProductCard/components/ProductDocuments'
import { useOfferDetails } from '@/components/ProductCard/useOfferDetails'
import { Features } from '@/utils/Features'
import { useFormatter } from '@/utils/useFormatter'
import { Alert } from 'ui/src/components/Alert/Alert'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { Card } from 'ui/src/components/Card/Card'
import { Divider } from 'ui/src/components/Divider/Divider'
import { Text } from 'ui/src/components/Text/Text'
import { Tooltip } from 'ui/src/components/Tooltip/Tooltip'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { useCarExtensionOffers } from './CarTrialExtensionOffers'
import { EditExtensionOfferForm } from './EditExtensionOfferForm'

export const TrialExtensionOfferCard = () => {
  const { t } = useTranslation(['cart', 'carDealership', 'purchase-form'])
  const formatter = useFormatter()

  const {
    extensionActivationDate,
    selectedOffer,
    isSigningWithoutExtension,
    isSigningWithExtension,
    removeExtensionOffer,
  } = useCarExtensionOffers()

  const productDetails = useOfferDetails(selectedOffer)

  if (isSigningWithoutExtension) {
    return null
  }

  const { variant } = selectedOffer
  const isTrafficTierWithoutAddons = Features.enabled('CAR_ADDONS') && selectedOffer.addons.length === 0

  return (
    <div className={yStack({ gap: 'md' })}>
      <Card.Root>
        {isSigningWithExtension && (
          <Tooltip.Root>
            <Card.Aside floating>
              <Tooltip.Trigger asChild>
                <IconButton variant="secondary" onClick={removeExtensionOffer}>
                  <CrossIcon />
                </IconButton>
              </Tooltip.Trigger>
            </Card.Aside>
            <Tooltip.Content sideOffset={5}>
              <Text size="xs" color="textNegative" align="center">
                {t('REMOVE_ENTRY_BUTTON')}
              </Text>
            </Tooltip.Content>
          </Tooltip.Root>
        )}

        <Card.Header className={xStack({ alignItems: 'center' })}>
          <Card.Media>
            <Pillow
              size="small"
              alt={selectedOffer.product.pillowImage.alt}
              role="presentation"
              src={selectedOffer.product.pillowImage.src}
            />
          </Card.Media>

          <Card.Heading>
            <Card.Title variant={{ _: 'standard.16', sm: 'standard.18' }}>
              {selectedOffer.product.displayNameFull}
            </Card.Title>
            <Card.Subtitle size={{ _: 'sm', sm: 'md' }}>
              {t('CART_ENTRY_DATE_LABEL', {
                date: formatter.shortDate(extensionActivationDate),
              })}
            </Card.Subtitle>
          </Card.Heading>
        </Card.Header>

        <Divider />

        <ProductDetails details={productDetails} />
        <ProductDocuments documents={variant.documents} />

        <EditExtensionOfferForm />
        {isTrafficTierWithoutAddons && (
          <Alert.Root variant="info">
            <Alert.Icon icon={InfoIcon} />
            <Alert.Body>
              <Alert.Message>{t('purchase-form:CAR_ADDON_TEASER_DESCRIPTION')}</Alert.Message>
            </Alert.Body>
          </Alert.Root>
        )}
        <Text color="textSecondary" size="xs" align="center">
          {t('carDealership:INSURANCE_NOTICE')}
        </Text>
      </Card.Root>
    </div>
  )
}
